import React, { useState } from 'react';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { MessageCircle, X, ArrowUpRight, Check } from 'lucide-react';
import { useRecaptchaToken, recaptchaHeaders, RecaptchaActions } from '@/lib/recaptcha';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QueryPopup = () => {
  const executeRecaptcha = useRecaptchaToken();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', mobile: '', company: '', message: '' });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill name, email and your query.');
      return;
    }
    if (form.message.trim().length < 5) {
      toast.error('Please share a bit more detail in your query.');
      return;
    }
    setLoading(true);
    try {
      const token = await executeRecaptcha(RecaptchaActions.contact);
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim() || null,
        company: form.company.trim() || null,
        message: form.message.trim(),
        purpose: 'General Query',
        source: 'query-popup',
      }, { headers: recaptchaHeaders(token, RecaptchaActions.contact) });
      setDone(true);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setForm({ name: '', email: '', mobile: '', company: '', message: '' });
    }, 350);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open query form"
        data-testid="query-popup-trigger"
        className="fixed z-[60] bottom-5 right-[5.5rem] md:bottom-7 md:right-[6.5rem] inline-flex items-center gap-2 px-4 md:px-5 py-3 bg-[#2D1810] text-white text-[11px] tracking-[0.22em] uppercase font-medium border border-[#B8884A]/40 rounded-full shadow-[0_14px_30px_-12px_rgba(58,36,24,0.45)] hover:bg-[#3A2418] transition-colors"
      >
        <MessageCircle size={15} className="text-[#D9B26A]" />
        <span className="hidden sm:inline">Have a Query?</span>
        <span className="sm:hidden">Query</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center" data-testid="query-popup-dialog">
          <button
            aria-label="Close"
            onClick={close}
            className="absolute inset-0 bg-[#1A0E08]/75 backdrop-blur-sm"
          />
          <Toaster position="top-center" toastOptions={{ style: { background: 'var(--bg-deep)', color: 'var(--ink)', border: '1px solid rgba(184, 136, 74, 0.4)', borderRadius: 0 } }} />
          <div className="relative w-full md:max-w-lg mx-auto bg-white border-t md:border border-[#B8884A]/35 shadow-[0_30px_80px_-20px_rgba(58,36,24,0.45)] max-h-[92vh] overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 px-6 md:px-8 pt-7 pb-5 border-b border-[#B8884A]/25 bg-white">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#9F6A2C]">Quick Connect</div>
                <h3 className="font-serif text-[var(--ink)] text-2xl md:text-3xl mt-1 leading-tight">
                  Have a <span className="italic text-[#9F6A2C]">Query?</span>
                </h3>
              </div>
              <button
                onClick={close}
                aria-label="Close"
                data-testid="query-popup-close"
                className="w-9 h-9 border border-[#B8884A]/35 flex items-center justify-center text-[#9F6A2C] hover:bg-[#9F6A2C] hover:text-white transition-colors flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>

            {done ? (
              <div className="px-6 md:px-10 py-14 text-center" data-testid="query-popup-success">
                <span className="w-12 h-12 inline-flex items-center justify-center border border-[#9F6A2C] text-[#9F6A2C]">
                  <Check size={20} />
                </span>
                <h4 className="font-serif text-[var(--ink)] text-2xl mt-6">Thanks! We&rsquo;ll be in touch.</h4>
                <p className="text-[var(--ink-muted)] text-sm mt-3 max-w-sm mx-auto leading-relaxed">
                  Your query has reached the team. We&rsquo;ll reply within 48 hours.
                </p>
                <button onClick={close} className="btn-ghost mt-8" data-testid="query-popup-done">Close</button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="px-6 md:px-8 py-7 space-y-4">
                <Row label="Your Name *" tid="query-name">
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    className="field-input"
                    data-testid="query-input-name"
                    autoComplete="name"
                  />
                </Row>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Row label="Email *" tid="query-email">
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => set('email', e.target.value)}
                      className="field-input"
                      data-testid="query-input-email"
                      autoComplete="email"
                    />
                  </Row>
                  <Row label="Mobile" tid="query-mobile">
                    <input
                      type="tel"
                      value={form.mobile}
                      onChange={(e) => set('mobile', e.target.value)}
                      className="field-input"
                      data-testid="query-input-mobile"
                      autoComplete="tel"
                    />
                  </Row>
                </div>
                <Row label="Company / Brand" tid="query-company">
                  <input
                    type="text"
                    value={form.company}
                    onChange={(e) => set('company', e.target.value)}
                    className="field-input"
                    data-testid="query-input-company"
                  />
                </Row>
                <Row label="Your Query *" tid="query-message">
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => set('message', e.target.value)}
                    className="field-input resize-y min-h-[110px]"
                    data-testid="query-input-message"
                    placeholder="Tell us what you'd like to know..."
                  />
                </Row>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="query-submit"
                  className="btn-solid w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Sending...' : (<>Send Query <ArrowUpRight size={14} /></>)}
                </button>
                <p className="text-[11px] text-[var(--ink-muted)] leading-relaxed text-center">
                  Or write to us at <a href="mailto:awards@marketingmind.co" className="text-[#9F6A2C] hover:underline">awards@marketingmind.co</a>
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const Row = ({ label, tid, children }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.25em] uppercase text-[#6B4F38] font-semibold mb-2">{label}</span>
    {children}
  </label>
);

export default QueryPopup;
